import domtoimage from 'dom-to-image';
import { getConfigXMLName, keyboardImagesNames, link_out } from '../../utils/DictionaryManagementUtils';
import extractServiceFlowJSON from '../ExportServicesConfigurations';
import extractKeyboardJSON, { extractBuildSettings } from '../ExportMainConfigurations';
import extractOnboardingJSON from '../OnBoarding';

const onboarding_dir = 'onboarding/';
const icons_dir = 'icons/';
const services_dir = 'services/';

const go_main_config_name = 'go_main_config';
const build_settings = 'build_settings';
const onboarding = 'onboarding';

const font_normal = 'font_normal';
const font_bold = 'font_bold';

export default function ExportFileManger(exportHandler, snapshotsOnboarding, isAndroid) {
  this.snapshots = snapshotsOnboarding;
  this.exportHandler = exportHandler;
  this.isAndroid = isAndroid;

  this.exportFiles = async (configurations, exportOptions) => {
    if (exportOptions.forOnboarding) {
      await this.exportOnboardingFiles(configurations, exportOptions);
    }
    if (exportOptions.forKeyboard) {
      await this.exportKeyboardFiles(configurations, exportOptions);
    }
  };

  this.exportKeyboardFiles = async (configurations, exportOptions) => {
    if (exportOptions.handleOther) {
      await this.exportServices(configurations);
      await this.exportMainConfig(configurations);
      await this.exportBuildSetting(configurations);
      await this.exportStrings(configurations);
      await this.exportFonts(configurations);
    }
    if (exportOptions.handleImages) {
      await this.exportKeyboardIcons(configurations);
    }
  };

  this.exportKeyboardIcons = async ({ launch, general, services }) => {
    const exportImageList = [
      { name: keyboardImagesNames.ic_keyboard, file: services.menu.keyboard_logo },
      { name: keyboardImagesNames.qab_menu_bank_logo, file: services.menu.hotkey_qab },
      { name: keyboardImagesNames.ic_keyboard_disabled, file: services.menu.keyboard_disabled_logo },
      { name: keyboardImagesNames.ic_bank_logo, file: services.menu.bank_logo },
      { name: keyboardImagesNames.ic_iMsg_bank_logo, file: services.menu.iMsg_bank_logo },
      { name: keyboardImagesNames.ic_no_results, file: launch.keyboard.no_results_image },
      { name: keyboardImagesNames.ic_verify_recipient, file: general.verify_recipient_icon },
      { name: keyboardImagesNames.ic_general_error, file: launch.error_messages.general_error_icon },
      { name: keyboardImagesNames.ic_connection_error, file: launch.error_messages.no_connection_icon },
      { name: keyboardImagesNames.ic_transaction_error, file: launch.error_messages.transaction_failed_icon },
      { name: keyboardImagesNames.ic_gallery_permission, file: launch.error_messages.permission_to_photos_icon },
      { name: keyboardImagesNames.ic_landscape_error, file: launch.error_messages.no_landscape_icon },
      { name: keyboardImagesNames.ic_empty_view_state, file: launch.empty_state.empty_state_icon },
      { name: keyboardImagesNames.ic_success, file: general.success_image },
    ];
    services.screens_ordering
      .filter((serviceKey) => services[serviceKey].selected)
      .forEach((serviceKey) => {
        exportImageList.push({
          name: `ic_${serviceKey}`,
          file: services[serviceKey].icon,
        });
        exportImageList.push({
          name: `ic_${services[serviceKey].sub_category}`,
          file: services[serviceKey].icon_action_bar,
        });
      });

    await this.exportIcons(exportImageList);
  };

  this.exportOnboardingFiles = async (configurations, exportOptions) => {
    if (exportOptions.handleOther) {
      await this.exportOnboardingJson(configurations);
      await this.exportOnboardingString(configurations);
      await this.exportFonts(configurations, true);
    }
    if (exportOptions.handleImages) {
      await this.exportOnboardingIcons(configurations.onboarding);
    }
  };

  this.exportServices = async (configurations) => {
    const promises = configurations.services.screens_ordering
      .filter((serviceKey) => configurations.services[serviceKey].selected && (configurations.services[serviceKey].sub_category !== link_out))
      .map((serviceKey) => this
        .exportHandler.exportJSON(
          extractServiceFlowJSON(configurations, configurations.services[serviceKey]),
          `${this.getBaseDirectory()}${this.getServicesDirectory()}${getConfigXMLName(serviceKey)}`,
        ));
    await Promise.all(promises);
  };

  this.exportMainConfig = (configurations) => this
    .exportHandler.exportJSON(extractKeyboardJSON(configurations), `${this.getBaseDirectory()}${go_main_config_name}`);

  this.exportBuildSetting = (configurations) => this
    .exportHandler.exportJSON(extractBuildSettings(configurations, this.isAndroid), `${this.getBaseDirectory()}${build_settings}`);

  this.exportOnboardingJson = (configurations) => this
    .exportHandler.exportJSON(extractOnboardingJSON(configurations), `${this.getBaseDirectory()}${this.getOnboardingDirectory()}${onboarding}`);

  this.exportFonts = async (configurations, isOnboarding = false) => {
    const promises = [];
    const { fonts } = configurations.theme;
    const exportFontList = [
      { name: font_normal, fontObj: fonts.custom_font },
      { name: font_bold, fontObj: fonts.custom_bold_font },
    ];

    exportFontList.forEach(({ name, fontObj }) => {
      if (fontObj.fUri) {
        promises.push(this.exportHandler.exportFont(
          fontObj.fUri,
          `${this.getBaseDirectory()}${isOnboarding ? this.getOnboardingDirectory() : ''}${name}`,
          fontObj.fName.split('.')[1],
        ));
      }
    });
    await Promise.all(promises);
  };

  this.exportIcons = async (exportImagesList, isOnboarding = false) => {
    const loadedImagesListPromises = [];
    exportImagesList.forEach(({ name, file }) => {
      if (file.fUri) {
        loadedImagesListPromises.push(this.loadImage(name, file.fUri));
      }
    });
    const loadedImages = await Promise.all(loadedImagesListPromises);
    const resizeImages = (this.skipResize && !isOnboarding) ? this.skipDeviceResizeImages(loadedImages) : this.deviceResizeImages(loadedImages);
    const exportedImagesListPromises = resizeImages.map(({ name, dataURL }) => this
      .exportHandler.exportImage(dataURL, `${this.getBaseDirectory()}${isOnboarding ? this.getOnboardingDirectory() : ''}${icons_dir}${name}`));
    await Promise.all(exportedImagesListPromises);
  };

  this.loadImage = (name, imageUri) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ name, img });
    img.onerror = reject;
    img.src = imageUri;
  });

  this.resizeImage = (img, name, multiplier = 1) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const { width, height } = img;
    const customWidth = multiplier * width;
    const customHeight = multiplier * height;

    // We set the dimensions at the wanted size.
    canvas.width = customWidth;
    canvas.height = customHeight;

    // We resize the image with the canvas method drawImage();
    ctx.drawImage(img, 0, 0, customWidth, customHeight);

    const dataURL = canvas.toDataURL();

    return { name, dataURL };
  };

  this.handleSnapshots = async (index) => {
    let imageUri;
    await domtoimage.toPng(this.snapshots[index].current).then((dataUrl1) => {
      imageUri = dataUrl1;
    });
    return imageUri;
  };

  this.getOnboardingDirectory = () => onboarding_dir;

  this.getServicesDirectory = () => services_dir;
}
